// rates.js
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

/**
 * Consulta la tasa de cambio para USD a VES desde Firestore.
 * Se espera que el documento tenga la siguiente estructura:
 * {
 *   "promedio": 71.75,
 *   ... (otros campos)
 * }
 *
 * @returns {Promise<number>} - El valor promedio (USD -> VES).
 */
export const getVesRate = async () => {
  try {
    const docRef = doc(db, "exchangeRates", "usd-ves");
    const docSnap = await getDoc(docRef);
    console.log(docSnap.data());
    if (docSnap.exists()) {
      return docSnap.data().promedio;
    } else {
      console.log(docSnap.data());
      throw new Error("Documento 'usd-ves' no existe en Firestore.");
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Consulta la tasa de cambio para USD a COP desde Firestore.
 * Se espera que el documento tenga la siguiente estructura:
 * {
 *   "compra": {
 *     "promedioUltimos7": "4035.37",
 *     ... (otros campos)
 *   },
 *   "venta": {
 *     "promedioUltimos7": "4012.85",
 *     ... (otros campos)
 *   }
 * }
 *
 * Para convertir de USD a COP se usa la tasa de venta (doc. venta.promedioUltimos7).
 *
 * @returns {Promise<string>} - El valor promedio para USD -> COP.
 */
export const getUsdCopRate = async () => {
  try {
    const docRef = doc(db, "exchangeRates", "usd-cop");
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data().venta.promedioUltimos7;
    } else {
      throw new Error("Documento 'usd-cop' no existe en Firestore.");
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Para convertir de COP a USD se utiliza la tasa de compra (doc. compra.promedioUltimos7).
 *
 * @returns {Promise<string>} - El valor promedio para COP -> USD.
 */
export const getCopUsdRate = async () => {
  try {
    const docRef = doc(db, "exchangeRates", "usd-cop");
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data().compra.promedioUltimos7;
    } else {
      throw new Error("Documento 'usd-cop' no existe en Firestore.");
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Consulta todas las tasas relevantes en paralelo.
 *
 * @returns {Promise<Object>} - Un objeto con las tasas obtenidas:
 *   - vesRate: Tasa USD -> VES.
 *   - vesUsdRate: Tasa VES -> USD (inversa de vesRate).
 *   - usdCopRate: Tasa USD -> COP (venta).
 *   - copUsdRate: Tasa COP -> USD (compra).
 */
export const getRates = async () => {
  const [vesRate, usdCopRate, copUsdRate] = await Promise.all([
    getVesRate(),
    getUsdCopRate(),
    getCopUsdRate()
  ]);
  return { 
    vesRate, 
    vesUsdRate: vesRate ? (1 / vesRate).toFixed(4) : null,
    usdCopRate, 
    copUsdRate 
  };
};
